import { motion, useReducedMotion } from 'framer-motion'
import { Users, AlertTriangle, CircleDollarSign, ClipboardList } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

type PainPoint = {
  icon: LucideIcon
  title: string
  description: string
  /** Color accent del card (texto, ring icon, tint overlay foto). */
  color: string
  ring: string
  /** RGB del color accent para mix-blend del overlay en la foto. */
  tintRgb: string
  /** Path local a imagen optimizada en /brand/. */
  image: string
  /** Alt text accesibilidad. */
  alt: string
}

const POINTS: PainPoint[] = [
  {
    icon: Users,
    title: 'Licencias médicas inesperadas',
    description: 'Ausencias que afectan la operación y los resultados.',
    color: 'text-fitlab-red',
    ring: 'ring-fitlab-red/20',
    tintRgb: '233, 75, 75',
    image: '/brand/painpoint-licencias.jpg',
    alt: 'Encargada de operaciones revisando agendas y pacientes pendientes por ausencias del equipo',
  },
  {
    icon: AlertTriangle,
    title: 'Trabajadores en riesgo sin diagnóstico',
    description: 'Problemas de salud que podrían derivar en accidentes o complicaciones mayores.',
    color: 'text-orange-500',
    ring: 'ring-orange-500/20',
    tintRgb: '249, 115, 22',
    image: '/brand/painpoint-trabajadores-riesgo.jpg',
    alt: 'Operario industrial con casco y chaleco reflectivo agotado en su puesto de trabajo',
  },
  {
    icon: CircleDollarSign,
    title: 'Costos ocultos por falta de prevención',
    description: 'Gastos que se pueden evitar con detección temprana.',
    color: 'text-fitlab-green',
    ring: 'ring-fitlab-green/20',
    tintRgb: '43, 191, 122',
    image: '/brand/painpoint-costos-ocultos.jpg',
    alt: 'Balanza comparando pérdidas por accidentes, ausentismo y multas vs ganancias por empleados saludables',
  },
  {
    icon: ClipboardList,
    title: 'Decisiones tomadas sin información clínica',
    description: 'Sin datos reales, las decisiones siempre serán a ciegas.',
    color: 'text-fitlab-blue',
    ring: 'ring-fitlab-blue/20',
    tintRgb: '30, 95, 201',
    image: '/brand/painpoint-decisiones-sin-datos.jpg',
    alt: 'Sala de reuniones ejecutiva con gráficos separada de los puestos de trabajo del equipo',
  },
]

/**
 * PainPoints — sección "Lo que no estás viendo hoy, ya está impactando tu empresa".
 *
 * Cada card: foto Unsplash genérica con tratamiento monocromo + tint del color
 * accent (rojo/naranja/verde/azul). En hover la foto recupera el color natural
 * (efecto reveal — invita a la interacción).
 *
 * Las fotos vienen del CDN de Unsplash con licencia comercial sin atribución.
 * No se commitean binarios — el navegador descarga directo del CDN con cache
 * agresivo y WebP automático.
 */
export default function PainPoints() {
  const reduced = useReducedMotion()

  return (
    <section
      id="beneficios"
      className="relative bg-fitlab-neutral-50 py-20 sm:py-28"
      aria-label="Problemas comunes en salud ocupacional"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.h2
          initial={reduced ? undefined : { opacity: 0, y: 16 }}
          whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="mx-auto max-w-3xl text-balance text-center text-2xl font-bold leading-tight tracking-tight text-fitlab-blue-hover sm:text-3xl lg:text-4xl"
        >
          Lo que no estás viendo hoy,{' '}
          <span className="text-fitlab-green-hover">ya está impactando tu empresa</span>
        </motion.h2>

        <ul role="list" className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {POINTS.map((p, i) => {
            const Icon = p.icon
            return (
              <motion.li
                key={p.title}
                initial={reduced ? undefined : { opacity: 0, y: 24 }}
                whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{
                  duration: 0.55,
                  delay: reduced ? 0 : i * 0.1,
                  ease: [0.16, 1, 0.3, 1],
                }}
                whileHover={reduced ? undefined : { y: -6 }}
                className="group relative flex flex-col overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-fitlab-neutral-100 transition-shadow hover:shadow-xl"
              >
                {/* Foto con tratamiento monocromo + tint accent */}
                <div className="relative aspect-[4/3] overflow-hidden bg-fitlab-neutral-100">
                  <img
                    src={p.image}
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                    alt={p.alt}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover grayscale brightness-90 transition-all duration-500 group-hover:grayscale-0 group-hover:brightness-100 group-hover:scale-105"
                  />
                  {/* Tint accent del color del card — mix-blend para fundir con la foto */}
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 opacity-60 mix-blend-multiply transition-opacity duration-500 group-hover:opacity-20"
                    style={{
                      background: `linear-gradient(180deg, rgba(${p.tintRgb}, 0.7) 0%, rgba(${p.tintRgb}, 0.4) 60%, rgba(${p.tintRgb}, 0.9) 100%)`,
                    }}
                  />
                  {/* Icono flotante sobre la foto */}
                  <motion.div
                    whileHover={reduced ? undefined : { scale: 1.12, rotate: -3 }}
                    transition={{ type: 'spring', stiffness: 320, damping: 16 }}
                    className={cn(
                      'absolute bottom-3 left-3 flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-lg ring-2',
                      p.ring,
                    )}
                  >
                    <Icon
                      className={cn('h-6 w-6', p.color)}
                      strokeWidth={1.75}
                      aria-hidden="true"
                    />
                  </motion.div>
                </div>

                {/* Texto */}
                <div className="flex flex-1 flex-col px-5 py-5">
                  <h3 className="text-base font-bold leading-tight text-fitlab-blue-hover">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-fitlab-neutral-600">
                    {p.description}
                  </p>
                </div>
              </motion.li>
            )
          })}
        </ul>

        <motion.p
          initial={reduced ? undefined : { opacity: 0, y: 16 }}
          whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.7 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="mx-auto mt-14 max-w-3xl rounded-xl bg-fitlab-blue/[0.07] px-6 py-5 text-center text-base font-semibold text-fitlab-blue-hover sm:text-lg"
        >
          Postergar la prevención hoy puede impactar tu operación mañana.
        </motion.p>
      </div>
    </section>
  )
}
